import type {
  CameraKeyframe, CameraConfig, Globe2CameraKeyframeEntry,
  Globe2CameraKeyframeMap, Globe2ResponsiveCanvasStyling,
} from "./globe2Types"

export type { Globe2CameraKeyframeMap }

export type Globe2CameraSettings = {
  fov: number
  near: number
  far: number
  autoRotationMax: number
  defaultEasingPower: number
  defaultEasingBias: number
  defaultDistance: number
  defaultTilt: number
  defaultLookAtOffset: { x: number; y: number; z: number }
}

const deg2rad = (d: number) => d * Math.PI / 180
const rad2deg = (r: number) => r * 180 / Math.PI
function toRadiansKeyframe(kf: CameraKeyframe): CameraKeyframe {
  return {
    ...kf,
    tilt: deg2rad(kf.tilt ?? cameraSettingsData.defaultTilt),
    earthRotation: deg2rad(kf.earthRotation ?? 0),
  }
}

export const cameraSettingsData: Globe2CameraSettings = {
  fov: 45,
  near: 0.1,
  far: 10000,
  defaultEasingPower: 2,
  defaultEasingBias: 0.5,
  defaultDistance: 2.8,
  defaultTilt: 35,
  defaultLookAtOffset: { x: 0, y: 0, z: 0 },
  autoRotationMax: 180
}

export const cameraKeyframesData: Globe2CameraKeyframeMap = {
  "2026-01-01": {
    cameraKeyframe: {
      distance: 4.5,
      lookAtOffset: { x: 0, y: 0, z: 0 },
      tilt: 30,
      earthRotation: -15,
      easingPower: 3,
      accelerationBias: 0.3
    },
    styling: { top: -20 },
    // medScreenOverride: {
    //   cameraKeyframe: { distance: 3 },
    //   styling: { top: -30 },
    // },
    // shortScreenOverride: {
    //   cameraKeyframe: { distance: 4 },
    //   styling: { top: '-4vh' },
    // }
  },
  "2027-01-01": {
    cameraKeyframe: {
      earthRotation: 12,
    },
  },
  "2028-01-01": {
    cameraKeyframe: {
      earthRotation: 45,
    },
    // styling: { top: '-3vh' },
    medScreenOverride: {
      // cameraKeyframe: { distance: 5.25 },
      // styling: { top: '-25vh' },
    },
    shortScreenOverride: {
      // cameraKeyframe: { distance: 5.25 },
      // styling: { top: '-17vh' },
    }
  },
  "2028-06-01": {
    cameraKeyframe: {
      earthRotation: 90,
    },
    // styling: { top: -30 },
    medScreenOverride: {
      // cameraKeyframe: { distance: 4 },
      // styling: { opacity: 0 }
    },
  },
  "2029-01-01": {
    cameraKeyframe: {
      earthRotation: 135,
    },
  },
  "2030-01-01": {
    cameraKeyframe: {
      earthRotation: 180,
    },
  },
  "2034-01-01": {
    cameraKeyframe: {
      earthRotation: 225,
    }
  },
  "2035-01-01": {
    settingOverride: {
      continentSettings: {
        coastlineWidth: 1
      }
    },
    // styling: { top: '-17vh' },
    cameraKeyframe: {
      // distance: 3.25,
      earthRotation: 300,
    },
  },
  "2037-01-01": {
    settingOverride: {
      continentSettings: {
        coastlineWidth: 1
      }
    },
    // styling: { top: -10 },
    cameraKeyframe: {
      earthRotation: 360,
      // distance: 4,
    },
  },
  "2038-01-01": {
    settingOverride: {
      continentSettings: {
        coastlineWidth: 1
      }
    },
    cameraKeyframe: {
      earthRotation: 405,
      // distance: 5,
    },
  },
  "2039-01-01": {
    settingOverride: {
      continentSettings: {
        coastlineWidth: 1
      }
    },
    cameraKeyframe: {
      // distance: 5.5,
      lookAtOffset: { x: 0, y: 0, z: 0 },
      tilt: 23,
      // Forward step from 2035's 305° (was 290°, a backward dip that forced a
      // ~345° "pirouette" respin). 320° keeps the spin uniform and < 270°.
      earthRotation: 495,
      easingPower: 3,
      accelerationBias: 0.3
    },
  },
  "2040-01-01": {
    settingOverride: {
      continentSettings: {
        coastlineWidth: 1
      }
    },
    cameraKeyframe: {
      earthRotation: 540,
      // distance: ,
    },
    // styling: { top: -220 }
  },
  // Epilogue opening ("Epilogue: Life after ASI" + the Space Annex section).
  // Reaching 2041 the Earth zooms away and — GLOBE_FADE_OUT_DELAY_MS after
  // the keyframe activates (see Globe2.tsx) — fades out. Returning to 2040
  // fades it back in immediately and flies the camera back in smoothly (the
  // reverse-scroll snap in useCamera.ts only fires from the farther 2042
  // framing below — see CAMERA_RESET_SNAP_MIN_DISTANCE).
  "2041-01-01": {
    settingOverride: {
      continentSettings: {
        coastlineWidth: 1
      }
    },
    cameraKeyframe: {
      distance: 500,
      easingPower: 3,
      accelerationBias: 0.3,
      lookAtOffset: { x: 0, y: 0, z: 0 },
      tilt: 23.44,
      earthRotation: 600,
    },
    styling: { opacity: 0, top: -30 }
  },
  "2041-06-01": {
    cameraKeyframe: {
      distance: 5000,
      easingPower: 3,
      accelerationBias: 0.3,
      lookAtOffset: { x: 0, y: 0, z: 0 },
      tilt: 23.44,
      earthRotation: 645,
    },
    styling: { opacity: 0, top: -30 }
  },
}

const defaultKeyframe = toRadiansKeyframe({
  distance: cameraSettingsData.defaultDistance,
  tilt: cameraSettingsData.defaultTilt,
  earthRotation: 0,
  lookAtOffset: cameraSettingsData.defaultLookAtOffset,
})

const optionalKeyframeDefaults: Partial<CameraKeyframe> = {
  easingPower: cameraSettingsData.defaultEasingPower,
  accelerationBias: cameraSettingsData.defaultEasingBias,
}

function fillKeyframeFromPrevious(
  kf: CameraKeyframe,
  lastFilled: Partial<CameraKeyframe>
): CameraKeyframe {
  const filled: CameraKeyframe = {
    distance: kf.distance,
    lookAtOffset: kf.lookAtOffset,
    tilt: kf.tilt,
    earthRotation: kf.earthRotation,
  }
  const optionalKeys = ['easingPower', 'accelerationBias', 'distanceEasing', 'tiltEasing', 'lookAtEasing', 'lookAtBias'] as const
  for (const key of optionalKeys) {
    const val = (kf as Record<string, unknown>)[key]
    if (val !== undefined) {
      (filled as Record<string, unknown>)[key] = val
    } else {
      const fromLast = (lastFilled as Record<string, unknown>)[key]
      const fromDefault = (optionalKeyframeDefaults as Record<string, unknown>)[key]
      ;(filled as Record<string, unknown>)[key] = fromLast ?? fromDefault
    }
  }
  return filled
}

function processKeyframesMap(keyframesMap: Globe2CameraKeyframeMap): Record<string, CameraKeyframe> {
  const sortedEntries = Object.entries(keyframesMap).sort(([a], [b]) => a.localeCompare(b))
  let lastFilled: Partial<CameraKeyframe> = { ...optionalKeyframeDefaults }
  const filledKeyframes: Record<string, CameraKeyframe> = {}
  for (const [key, entry] of sortedEntries) {
    const raw = entry.cameraKeyframe ?? {}
    const merged: CameraKeyframe = {
      ...raw,
      distance: raw.distance ?? lastFilled.distance ?? cameraSettingsData.defaultDistance,
      lookAtOffset: raw.lookAtOffset ?? lastFilled.lookAtOffset ?? cameraSettingsData.defaultLookAtOffset,
      tilt: raw.tilt ?? (lastFilled.tilt !== undefined ? rad2deg(lastFilled.tilt) : cameraSettingsData.defaultTilt),
      earthRotation: raw.earthRotation ?? (lastFilled.earthRotation !== undefined ? rad2deg(lastFilled.earthRotation) : 0),
    }
    const kf = toRadiansKeyframe(merged)
    const filled = fillKeyframeFromPrevious(kf, lastFilled)
    lastFilled = filled
    filledKeyframes[key] = filled
  }
  return filledKeyframes
}

export const cameraConfig: CameraConfig = {
  fov: cameraSettingsData.fov,
  near: cameraSettingsData.near,
  far: cameraSettingsData.far,
  position: [0, 0, defaultKeyframe.distance ?? cameraSettingsData.defaultDistance],
  autoRotationMax: deg2rad(cameraSettingsData.autoRotationMax),
  defaultEasingPower: cameraSettingsData.defaultEasingPower,
  defaultKeyframe,
}

export const processedCameraKeyframes: Record<string, CameraKeyframe> =
  processKeyframesMap(cameraKeyframesData)

export { processKeyframesMap }

const defaultStyling: Globe2ResponsiveCanvasStyling = {}

function processKeyframesMapWithStyling(keyframesMap: Globe2CameraKeyframeMap): Globe2CameraKeyframeMap {
  const sortedEntries = Object.entries(keyframesMap).sort(([a], [b]) => a.localeCompare(b))
  let lastStyling: Globe2ResponsiveCanvasStyling = defaultStyling
  const result: Globe2CameraKeyframeMap = {}
  for (const [key, entry] of sortedEntries) {
    const styling = entry.styling ?? lastStyling
    if (entry.styling) lastStyling = entry.styling
    result[key] = { ...entry, styling }
  }
  return result
}

export { processKeyframesMapWithStyling }

type ResponsiveOverrideKey = 'medScreenOverride' | 'shortScreenOverride'

function applyResponsiveKeyframeOverride(
  entry: Globe2CameraKeyframeEntry,
  overrideKey: ResponsiveOverrideKey
): Globe2CameraKeyframeEntry {
  const override = entry[overrideKey]
  return {
    ...entry,
    cameraKeyframe: { ...entry.cameraKeyframe, ...override?.cameraKeyframe },
    ...(override?.styling && { styling: { ...entry.styling, ...override.styling } }),
  }
}

export function applyResponsiveKeyframeOverrides(
  map: Globe2CameraKeyframeMap,
  overrideKeysInOrder: readonly ResponsiveOverrideKey[]
): Globe2CameraKeyframeMap {
  return Object.fromEntries(Object.entries(map).map(([key, entry]) => {
    let nextEntry = entry
    for (const overrideKey of overrideKeysInOrder) {
      nextEntry = applyResponsiveKeyframeOverride(nextEntry, overrideKey)
    }
    return [key, nextEntry]
  }))
}
